import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/utils/api';
import { formatCurrency } from '@/utils/helper';

/** @type {import('@mui/system').SxProps<import('@mui/material').Theme>} */
const defaultSx = [];
export default function PublicMilestoneTracker({
  eventId,
  userAmount = 0,
  sx: sxProp = defaultSx,
}) {
  const { t, i18n } = useTranslation();

  const [{ milestones, donationTotal }, setMilestoneInfo] = useState({
    milestones: [],
    donationTotal: 0,
  });
  useEffect(() => {
    api
      .get(`/api/v2/milestones/list/?event=${eventId}`)
      .then(response => setMilestoneInfo(response.data))
      // eslint-disable-next-line no-console -- TODO: global snackbar alerts
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only once
  }, []);
  if (!milestones || milestones.length === 0) return null;

  const nextMilestone = milestones.find(milestone => {
    const goal = Number.parseFloat(milestone.amount);

    return milestone.status !== 'performed' && donationTotal < goal;
  });
  if (!nextMilestone) return null;

  const goal = Number.parseFloat(nextMilestone.amount);
  const progress = Math.min((donationTotal / goal) * 100, 100);
  const title = i18n.language === 'en' ? nextMilestone.titleEn : nextMilestone.titleFr;

  const projectedTotal = donationTotal + userAmount;
  const projectedProgress = Math.min((projectedTotal / goal) * 100, 100);
  const projectedSegment = Math.max(projectedProgress - progress, 0);
  const wouldReach = userAmount > 0 && projectedTotal >= goal;

  return (
    <Box
      sx={[
        {
          width: '100%',
          padding: 1,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '2px',
          border: '1px solid rgba(255,255,255,0.08)',
          backgroundColor: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(12px)',
          borderTop: '2px solid',
          borderTopColor: 'warning.main',
          boxShadow: theme =>
            `0 -4px 20px -4px ${theme.palette.warning.main}30, inset 0 1px 0 rgba(255,255,255,0.05)`,
          // Diagonal scanline overlay - like a CRT monitor effect because I'm 8 and this is cool
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            opacity: 0.03,
            backgroundImage:
              'repeating-linear-gradient(135deg, transparent, transparent 2px, rgba(255,255,255,1) 2px, rgba(255,255,255,1) 3px)',
            pointerEvents: 'none',
          },
        },
        ...(Array.isArray(sxProp) ? sxProp : [sxProp]),
      ]}
    >
      {/* Header row */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          px: 1.5,
          paddingTop: 1,
          paddingBottom: 0.5,
        }}
      >
        <Typography
          className="font-bebas"
          sx={{
            fontSize: '0.75rem',
            letterSpacing: '0.15em',
            color: 'warning.main',
            textTransform: 'uppercase',
          }}
        >
          {t('milestone.nextGoal')}
        </Typography>
        <Typography
          className="font-bebas"
          sx={{
            fontSize: '0.75rem',
            letterSpacing: '0.08em',
            color: 'rgba(255,255,255,0.6)',
          }}
        >
          {formatCurrency(donationTotal, i18n.language)} / {formatCurrency(goal, i18n.language)}
        </Typography>
      </Box>

      {/* Milestone title */}
      <Box
        sx={{
          position: 'relative',
          px: 1.5,
          paddingBottom: 0.5,
        }}
      >
        <Typography
          className="font-oswald"
          sx={{
            marginBottom: 1.5,
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.2,
            color: 'white',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box
        sx={{
          position: 'relative',
          px: 1.5,
          paddingBottom: 1.5,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            overflow: 'hidden',
            width: '100%',
            height: 6,
            borderRadius: '1px',
            backgroundColor: 'rgba(255,255,255,0.08)',
          }}
        >
          {/* Current progress */}
          <Box
            sx={{
              width: `${progress}%`,
              height: '100%',
              backgroundColor: wouldReach ? 'success.main' : 'warning.main',
              boxShadow: theme =>
                `0 0 8px ${wouldReach ? theme.palette.success.main : theme.palette.warning.main}60`,
              transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s ease',
            }}
          />
          {/* Projected segment */}
          {userAmount > 0 && projectedSegment > 0 && (
            <Box
              sx={[
                {
                  width: `${projectedSegment}%`,
                  height: '100%',
                  transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  backgroundColor: 'warning.light',
                  opacity: 0.45,
                  // Pulsing stripe for projected amount
                  backgroundImage: 'none',
                },
                wouldReach && {
                  backgroundColor: 'success.main',
                  opacity: 0.7,
                  boxShadow: theme => `0 0 10px ${theme.palette.success.main}50`,
                },
              ]}
            />
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginTop: 0.5,
          }}
        >
          <Typography
            className="font-bebas"
            sx={[
              {
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
                fontWeight: 500,
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.6)',
              },
              wouldReach && {
                color: 'success.light',
                '@keyframes goalPulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.6 },
                },
                animation: 'goalPulse 2s ease-in-out infinite',
              },
            ]}
          >
            {wouldReach
              ? t('milestone.yourDonationReachesGoal')
              : t('milestone.missingToGoal', {
                  amount: formatCurrency(Math.max(goal - projectedTotal, 0), i18n.language),
                })}
          </Typography>
          <Typography
            className="font-bebas"
            sx={{
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
              color: wouldReach ? 'success.light' : 'warning.main',
              transition: 'color 0.3s ease',
            }}
          >
            {projectedProgress.toFixed(1)}%
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

PublicMilestoneTracker.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  userAmount: PropTypes.number,
};
